import React, { useState } from 'react';
import AdminButton from '../AdminButton';
import { fetchWithAuth } from '../../../api/apiClient';
import { BASE_URL } from '../../../api/inquiries';

const AiImageGeneratorModal = ({ isOpen, onClose, onInsert }) => {
    const [prompt, setPrompt] = useState(`Atec's state-of-the-art electronic shelf label (ESL) displaying digital pricing, placed on a modern supermarket shelf. High-end product photography, brightly lit, photorealistic, 8k resolution, clean background.`);
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [isLoading, setIsLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    if (!isOpen) return null;

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            alert('생성할 이미지에 대한 묘사를 입력해주세요.');
            return;
        }

        setIsLoading(true);
        setPreviewUrl(null);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/generate-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt, aspectRatio })
            });

            if (!res.ok) throw new Error('API Request Failed');

            const reqData = await res.json();
            if (reqData.success && reqData.url) {
                setPreviewUrl(reqData.url); // Show preview
            } else {
                alert('생성 실패: ' + (reqData.error || '알 수 없는 오류'));
            }
        } catch (error) {
            console.error('AI Image Generation error:', error);
            alert('AI 이미지 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInsert = () => {
        if (previewUrl) {
            onInsert(previewUrl);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[99] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                    <h3 className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-blue-500 m-0">
                        🎨 AI 이미지 생성기 (Imagen)
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent border-0 outline-none cursor-pointer">&times;</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 text-sm text-blue-800 font-medium">
                        원하시는 이미지의 묘사를 자유롭게 적어주세요. 구글의 최상위급 이미지 생성 모델인 <strong>Imagen 3</strong>는 <strong>텍스트 묘사 기반(Text-to-Image)</strong> 모델로만 작동합니다.<br/>
                        (※ 사용자가 미리 업로드한 이미지를 참조하여 합성하는 기능은 지원하지 않습니다)
                    </div>
                    
                    <div className="flex gap-4 items-center bg-gray-50 p-3 rounded-xl border border-gray-200">
                        <label className="text-sm font-bold text-gray-700 shrink-0">생성할 비율 선택</label>
                        <select 
                            value={aspectRatio} 
                            onChange={(e) => setAspectRatio(e.target.value)}
                            className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 outline-none font-medium shadow-sm"
                        >
                            <option value="1:1">1:1 (기본 정방형)</option>
                            <option value="4:3">4:3 (가로형)</option>
                            <option value="16:9">16:9 (와이드 가로형)</option>
                            <option value="3:4">3:4 (세로형)</option>
                            <option value="9:16">9:16 (와이드 세로형)</option>
                        </select>
                    </div>

                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="입력 예시: A professional product photography of Atec's smart electronic shelf label (ESL)..."
                        className="w-full h-28 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none resize-none text-sm text-gray-700 shadow-inner leading-relaxed"
                    />

                    {previewUrl && (
                        <div className="mt-4 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50 flex flex-col items-center gap-3">
                            <span className="text-sm font-bold text-gray-500">생성된 이미지 미리보기</span>
                            <img src={`${BASE_URL.replace('http://localhost:4000', '')}${previewUrl}`} alt="AI Generated" className="h-48 object-contain rounded-lg shadow-sm border border-gray-200" />
                            <AdminButton variant="primary" onClick={handleInsert}>✅ 이 이미지 사용하기</AdminButton>
                        </div>
                    )}
                </div>
                <div className="flex gap-3 p-6 pt-0 border-t border-gray-100 bg-gray-50/50">
                    <AdminButton variant="secondary" onClick={onClose} fullWidth>닫기</AdminButton>
                    <AdminButton variant="primary" onClick={handleGenerate} disabled={isLoading} fullWidth className="!bg-gradient-to-r from-primary-600 to-blue-500 hover:opacity-90 border-0 shadow-md">
                        {isLoading ? '⏳ 이미지 생성 중 (약 5~15초 소요)...' : '✨ 이미지 생성 시작'}
                    </AdminButton>
                </div>
            </div>
        </div>
    );
};

export default AiImageGeneratorModal;
